/**
 * commentary-entity-search.service.ts — DLITE-3849 Task 10
 *
 * Backs the commentary-ui drawer's `entitySearch` callback (the entity-link
 * type-ahead). The drawer asks Helios "what entities match this query?" and
 * Helios answers from the rows already loaded in the visible grids — no extra
 * server round-trip.
 *
 * Each commentary-enabled grid registers a "row source" keyed by its panel
 * key: a function that returns the grid's CURRENTLY LOADED rows already mapped
 * to CommentaryEntityRef[] (the grid owns the row→ref mapping because it knows
 * its own id fields). The source is re-invoked on every search so results
 * always reflect live grid state; grids unregister on destroy.
 *
 * `search` unions every source, filters by case-insensitive substring on `id`
 * (and exact `type` when supplied), de-dupes by `type:id`, and caps the count
 * so the drawer's dropdown stays bounded. Returns a Promise to match the
 * web-component contract `(query, type?) => Promise<EntityRef[]>`, even though
 * the work is synchronous today.
 */
import { Injectable } from '@angular/core'
import { CommentaryEntityRef, CommentaryEntityType } from '../types/thread-jump'

/** A grid's contribution to the search corpus: its current rows as refs. */
export type EntityRowSource = () => CommentaryEntityRef[]

/** Max refs returned from a single search — keeps the drawer dropdown bounded. */
const SEARCH_RESULT_CAP = 20

@Injectable({ providedIn: 'root' })
export class CommentaryEntitySearchService {
  private readonly sources = new Map<string, EntityRowSource>()

  /**
   * Register (or replace) a grid's row source under its panel key. Re-using
   * the same key overwrites — so a grid re-init after destroy is safe.
   */
  register(panelKey: string, source: EntityRowSource): void {
    this.sources.set(panelKey, source)
  }

  /** Drop a grid's source — called from the grid's ngOnDestroy. */
  unregister(panelKey: string): void {
    this.sources.delete(panelKey)
  }

  /**
   * Match registered rows by case-insensitive substring on `id`, optionally
   * constrained to a single entity `type`. De-duped by `type:id`, capped at
   * SEARCH_RESULT_CAP. A blank query returns [] (the drawer shows nothing
   * until the user types).
   */
  async search(
    query: string,
    type?: CommentaryEntityType,
  ): Promise<CommentaryEntityRef[]> {
    const needle = query?.trim().toLowerCase() ?? ''
    if (needle.length === 0) return []

    const seen = new Set<string>()
    const matches: CommentaryEntityRef[] = []

    for (const source of this.sources.values()) {
      let rows: CommentaryEntityRef[]
      try {
        rows = source() ?? []
      } catch {
        // A misbehaving source must not blank out the whole search.
        continue
      }

      for (const row of rows) {
        if (matches.length >= SEARCH_RESULT_CAP) {
          return matches
        }
        if (row == null || row.id == null) continue
        if (type != null && row.type !== type) continue
        if (!row.id.toLowerCase().includes(needle)) continue

        const dedupeKey = `${row.type}:${row.id}`
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)
        // Carry the optional friendly label (DLITE-3911) so commentary-ui can
        // render it on the chip; the id stays the stable jump/lookup key.
        matches.push({ type: row.type, id: row.id, label: row.label })
      }
    }

    return matches
  }
}
